// contexts/WebSocketContext.jsx
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { toast } from "react-toastify";

const WebSocketContext = createContext();

export const WebSocketProvider = ({ children }) => {
  const wsRef = useRef(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [socketError, setSocketError] = useState(false);
  const [socketData, setSocketData] = useState(null);

  useEffect(() => {
    let shouldReconnect = true;
    let reconnectTimeout = null;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 10;

    const connectWebSocket = () => {
      reconnectAttempts++;

      try {
        const ws = new WebSocket(
          `${process.env.REACT_APP_QUESTIONBROADCAST_SOCKET_URL}?token=${process.env.REACT_APP_QUESTIONBROADCAST_TOKEN}`
        );

        ws.onopen = () => {
          reconnectAttempts = 0;
          setIsSocketConnected(true);
          setSocketError(false);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            setSocketData((prevData) => {
              if (
                prevData &&
                prevData.type === data.type &&
                JSON.stringify(prevData.data) === JSON.stringify(data.data)
              ) {
                return prevData;
              }
              return data;
            });
          } catch (parseError) {
            console.error("❌ خطا در پارس کردن داده:", parseError);
          }
        };

        ws.onerror = (error) => {
          console.error("❌ خطای WebSocket:", error);
          setIsSocketConnected(false);
          setSocketError(true);
        };

        ws.onclose = (event) => {
          wsRef.current = null;
          setIsSocketConnected(false);

          if (shouldReconnect && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
            reconnectTimeout = setTimeout(() => {
              connectWebSocket();
            }, 3000);
          } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            console.error("❌ حداکثر تلاش برای اتصال مجدد انجام شد");
            setSocketError(true);
            toast.error(
              "❌ اتصال به سرور برقرار نشد. لطفاً صفحه را رفرش کنید."
            );
          }
        };

        wsRef.current = ws;
      } catch (error) {
        console.error("❌ خطا در ایجاد WebSocket:", error);
        setSocketError(true);
        setIsSocketConnected(false);
        toast.error("❌ خطا در اتصال به سرور. URL نامعتبر است.");

        if (shouldReconnect && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          reconnectTimeout = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      }
    };

    connectWebSocket();

    return () => {
      shouldReconnect = false;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (error) {
          console.error("❌ خطا در بستن WebSocket:", error);
        }
      }
    };
  }, []);

  const value = useMemo(
    () => ({
      wsRef,
      isSocketConnected,
      socketError,
      socketData,
    }),
    [isSocketConnected, socketError, socketData]
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
